import { TenantESignConfig } from '@celito.clients/endpoints';
import type { WidgetOptions } from '@okta/okta-signin-widget';

/**
 * WI-F7 — Okta widget config from runtime tenant config (ADO #38535).
 *
 * Previously this module read `OKTA_ISSUER` / `OKTA_CLIENT_ID` /
 * `OKTA_REDIRECT_URI` from build-time env (`VITE_OKTA_*`). Two problems:
 *   1. Build-time env cannot be per-tenant (one build serves many tenants).
 *   2. `OKTA_REDIRECT_URI` was hardcoded to a FOREIGN Dynamics URL.
 *
 * Now the issuer + clientId come from the runtime `TenantConfig.esign`, and the
 * redirect is derived from the page origin at call time (the tenant is always
 * served from its own origin, which is what Okta must redirect back to).
 */

/**
 * The origin-derived redirect URI. Kept as a function so it is evaluated at
 * sign-in time (not module import), and so tests can inject a value.
 */
export const getOktaRedirectUri = (): string =>
  typeof window !== 'undefined' ? window.location.origin : '';

/**
 * Build the Okta Sign-In Widget options from the tenant's runtime e-sign
 * config. `issuer` / `clientId` come from `TenantConfig.esign`; `redirectUri`
 * is origin-derived (no build-time env, no hardcoded URL).
 */
export const buildOktaWidgetConfig = (
  esign: Pick<TenantESignConfig, 'issuer' | 'clientId'>,
  redirectUri: string = getOktaRedirectUri()
): WidgetOptions => ({
  baseUrl: esign.issuer ?? '',
  clientId: esign.clientId ?? '',
  redirectUri,
  useInteractionCodeFlow: true,
  authParams: {
    issuer: esign.issuer ?? '',
    // The Middleware verifies the ID token and matches the Okta signer on the
    // `email` claim; pin the scopes rather than rely on okta-auth-js defaults.
    scopes: ['openid', 'email'],
  },
  features: {
    rememberMe: false,
  },
  i18n: {
    en: {
      'errors.E0000004':
        'Login verification was unsuccessful. Please try again.',
    },
  },
});
